import React, { useEffect, useState } from "react";
import axios from "axios";
import DashboardLayout from "../../components/Dashboard/DashboardLayout";
import CreateSubject from "./Subject/CreateSubject";
import ViewSubject from "./Subject/ViewSubject";
import CreateQuestion from "./QuationManagement/CreateQuestion";
import ViewQuestion from "./QuationManagement/ViewQuestion";
import CreateNote from "./Note/CreateNote";
import ViewNotes from "./Note/ViewNotes";
import ViewFlash from "./Flash/ViewFlash";
import CreateModule from "./Module/CreateModule";
import ViewModule from "./Module/ViewModule";
import IeltsManager from "./Ielts/IeltsManager";
import WritingSubmissions from "./Ielts/WritingSubmissions";
import IeltsResultManager from "./Ielts/IeltsResultManager";
import ViewUsers from "./User/ViewUsers";
import { config } from "../../config";
import {
  FaBook,
  FaGraduationCap,
  FaHeadphones,
  FaPenAlt,
  FaStickyNote,
  FaUsers,
  FaArrowRight,
  FaLayerGroup,
  FaRegCalendarAlt,
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";

// ─── route map ────────────────────────────────────────────────────────────────
const usePages = (setSelectedMenu) => ({
  addSubject: <CreateSubject onSubjectCreated={() => setSelectedMenu("subjectOverView")} />,
  subjectOverView: <ViewSubject />,
  addModule: <CreateModule onModuleCreated={() => setSelectedMenu("moduleOverView")} />,
  moduleOverView: <ViewModule />,
  addQuestion: <CreateQuestion onQuestionCreated={() => setSelectedMenu("questionOverview")} />,
  questionOverview: <ViewQuestion />,
  addNote: <CreateNote onNoteCreated={() => setSelectedMenu("noteOverview")} />,
  noteOverview: <ViewNotes />,
  flashOverview: <ViewFlash />,
  ieltsManager: <IeltsManager />,
  ieltsWriting: <WritingSubmissions />,
  ieltsResults: <IeltsResultManager />,
  users: <ViewUsers />,
});

// ─── root component ───────────────────────────────────────────────────────────
const DashboardHome = () => {
  const [selectedMenu, setSelectedMenu] = useState(
    () => localStorage.getItem("selectedMenu") || "dashboard"
  );

  useEffect(() => {
    localStorage.setItem("selectedMenu", selectedMenu);
  }, [selectedMenu]);

  const pages = usePages(setSelectedMenu);

  const content =
    selectedMenu === "dashboard" || !pages[selectedMenu] ? (
      <DashboardOverview setSelectedMenu={setSelectedMenu} />
    ) : (
      <div className="p-4 md:p-6">{pages[selectedMenu]}</div>
    );

  return (
    <DashboardLayout selectedMenu={selectedMenu} setSelectedMenu={setSelectedMenu}>
      {content}
    </DashboardLayout>
  );
};

// ─── dashboard overview ───────────────────────────────────────────────────────
const DashboardOverview = ({ setSelectedMenu }) => {
  const [stats, setStats] = useState({
    questions: "—",
    subjects: "—",
    users: "—",
    ielts: "—",
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    const h = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([
      axios.get(`${config.apiUrl}/questionmanagement`, { headers: h }).catch(() => ({ data: [] })),
      axios.get(`${config.apiUrl}/subjects`, { headers: h }).catch(() => ({ data: [] })),
      axios.get(`${config.apiUrl}/users`, { headers: h }).catch(() => ({ data: [] })),
      axios.get(`${config.apiUrl}/ielts-tests`, { headers: h }).catch(() => ({ data: [] })),
    ]).then(([q, s, u, i]) => {
      const len = (r) => (Array.isArray(r.data) ? r.data.length : r.data?.total ?? 0);
      setStats({ questions: len(q), subjects: len(s), users: len(u), ielts: len(i) });
    });
  }, []);

  const today = new Date().toLocaleDateString("en-BD", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="p-4 md:p-8 space-y-8">
      {/* Welcome banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 p-6 md:p-8 text-white shadow-xl">
        {/* decorative circles */}
        <div className="pointer-events-none absolute -top-10 -right-10 h-48 w-48 rounded-full bg-white/10" />
        <div className="pointer-events-none absolute -bottom-16 -right-4 h-64 w-64 rounded-full bg-white/5" />

        <div className="relative">
          <div className="flex items-center gap-2 mb-1 text-indigo-200 text-sm">
            <FaRegCalendarAlt />
            {today}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mb-1">Welcome back, Admin! 👋</h1>
          <p className="text-indigo-100 mb-6">
            Here's what's happening on your learning platform today.
          </p>
          <div className="flex flex-wrap gap-3">
            <NavBtn icon={FaPenAlt} label="Create Question" onClick={() => setSelectedMenu("addQuestion")} />
            <NavBtn icon={FaHeadphones} label="Manage IELTS" onClick={() => setSelectedMenu("ieltsManager")} />
            <NavBtn icon={FaUsers} label="View Users" onClick={() => setSelectedMenu("users")} />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Questions"
          value={stats.questions}
          icon={FaPenAlt}
          gradient="from-blue-500 to-cyan-400"
          onClick={() => setSelectedMenu("questionOverview")}
        />
        <StatCard
          label="Subjects"
          value={stats.subjects}
          icon={FaGraduationCap}
          gradient="from-purple-500 to-pink-400"
          onClick={() => setSelectedMenu("subjectOverView")}
        />
        <StatCard
          label="IELTS Tests"
          value={stats.ielts}
          icon={FaHeadphones}
          gradient="from-emerald-500 to-teal-400"
          onClick={() => setSelectedMenu("ieltsManager")}
        />
        <StatCard
          label="Users"
          value={stats.users}
          icon={FaUsers}
          gradient="from-orange-500 to-amber-400"
          onClick={() => setSelectedMenu("users")}
        />
      </div>

      {/* Quick actions + Feature cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick actions */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <SectionHeader icon={HiSparkles} title="Quick Actions" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <QuickAction
              icon={FaPenAlt}
              color="bg-blue-50 text-blue-600"
              title="Create Question"
              desc="Add new MCQ to any subject"
              onClick={() => setSelectedMenu("addQuestion")}
            />
            <QuickAction
              icon={FaHeadphones}
              color="bg-purple-50 text-purple-600"
              title="IELTS Practice"
              desc="Listening, reading, writing"
              onClick={() => setSelectedMenu("ieltsManager")}
            />
            <QuickAction
              icon={FaStickyNote}
              color="bg-emerald-50 text-emerald-600"
              title="Manage Notes"
              desc="Create study notes"
              onClick={() => setSelectedMenu("addNote")}
            />
            <QuickAction
              icon={FaBook}
              color="bg-amber-50 text-amber-600"
              title="Flash Cards"
              desc="Quick revision cards"
              onClick={() => setSelectedMenu("flashOverview")}
            />
            <QuickAction
              icon={FaLayerGroup}
              color="bg-pink-50 text-pink-600"
              title="Add Subject"
              desc="Create a new subject"
              onClick={() => setSelectedMenu("addSubject")}
            />
            <QuickAction
              icon={FaUsers}
              color="bg-indigo-50 text-indigo-600"
              title="Manage Users"
              desc="View all students"
              onClick={() => setSelectedMenu("users")}
            />
          </div>
        </div>

        {/* Platform summary */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <SectionHeader icon={FaLayerGroup} title="Platform" />
          <div className="mt-4 space-y-3">
            {[
              { icon: FaPenAlt, label: "MCQ Questions", sub: `${stats.questions} total` },
              { icon: FaGraduationCap, label: "Subjects", sub: `${stats.subjects} active` },
              { icon: FaHeadphones, label: "IELTS Tests", sub: `${stats.ielts} tests` },
              { icon: FaUsers, label: "Students", sub: `${stats.users} registered` },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <item.icon className="text-indigo-600 text-sm" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── small pieces ─────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon: Icon, gradient, onClick }) => (
  <button
    onClick={onClick}
    className="group relative overflow-hidden rounded-2xl bg-white border border-gray-200 shadow-sm p-5 text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
  >
    <div className={`inline-flex p-2.5 rounded-xl bg-gradient-to-br ${gradient} text-white mb-3`}>
      <Icon className="text-lg" />
    </div>
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
    <p className="text-3xl font-bold text-gray-900 mt-0.5">{value}</p>
    <FaArrowRight className="absolute bottom-4 right-4 text-gray-300 group-hover:text-gray-400 transition-colors" />
  </button>
);

const QuickAction = ({ icon: Icon, color, title, desc, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-3 p-4 rounded-xl border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/40 transition-all group text-left"
  >
    <div className={`w-9 h-9 rounded-lg ${color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
      <Icon className="text-base" />
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-800">{title}</p>
      <p className="text-xs text-gray-500">{desc}</p>
    </div>
  </button>
);

const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2">
    <Icon className="text-indigo-500 text-lg" />
    <h2 className="font-bold text-gray-800">{title}</h2>
  </div>
);

const NavBtn = ({ icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105"
  >
    <Icon className="text-sm" />
    {label}
  </button>
);

export default DashboardHome;
